/**
 * Parsed FAQ section - common structure for all FAQ consumers.
 */
export interface ParsedFAQSection {
  /** Slug derived from title, e.g., 'what-is-lyra-health' */
  slug: string
  /** The question/title text without ## prefix */
  title: string
  /** The answer/body content */
  content: string
  /** Original markdown section including ## header (for rendering in QuestionGenerator) */
  rawSection: string
}

export interface ParseFaqSectionsOptions {
  fallbackTitle?: (index: number) => string
}

/**
 * Generates a slug from a string.
 * Used to create unique IDs for FAQ items based on the question text.
 *
 * @param text
 * @returns A lowercase, hyphen-separated slug
 *
 * @example
 * generateFaqSlug('What is Lyra Health?') // 'what-is-lyra-health'
 * generateFaqSlug('How much does it cost?') // 'how-much-does-it-cost'
 */
export const generateFaqSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

/**
 * Parses FAQ markdown text into ParsedFAQSection array.
 * This is the single source of truth for FAQ parsing.
 *
 * The FAQ text format uses markdown with ## headers for questions:
 * ```
 * ## What is Lyra Health?
 *
 * Lyra is a benefit that connects employees...
 *
 * ## Who can use Lyra?
 *
 * Lyra is available to all employees...
 * ```
 *
 * @param faqText - The raw FAQ markdown text (e.g., from customerPropertiesMap.faqText)
 * @param options - Optional configuration (e.g., fallbackTitle for empty titles)
 * @returns Array of ParsedFAQSection objects
 */
export const parseFaqSections = (
  faqText: string | undefined,
  options?: ParseFaqSectionsOptions,
): ParsedFAQSection[] => {
  if (!faqText) {
    return []
  }

  // Split by ## headers (each FAQ question starts with ##)
  const sections = faqText.split(/(?=^## )/gm).filter((section) => section.trim())

  return sections.map((section, index) => {
    // Split by any line break style (\r\n, \r, or \n)
    const lines = section.trim().split(/\r\n|\r|\n/)
    const title = lines[0]?.replace(/^## /, '').trim() || options?.fallbackTitle?.(index) || ''
    const content = lines.slice(1).join('\n').trim()
    const slug = generateFaqSlug(title)

    return {
      slug,
      title,
      content,
      rawSection: section.trim(),
    }
  })
}
